<script setup>
/**
 * GiftLetter: one reading made for someone, as one letter, said once
 * (2026-10-19, Chris; design: Claude outputs/gift-page-design-2026-10-19).
 *
 * The picture; the sender's note tucked over its edge under a gold seal
 * ("<From> was thinking of you", the email's own first line); the title, once;
 * then each card once (art, position, card name, its short phrase, its
 * passage), strung on one gold thread that ends in a knot at "All four
 * together". Presentational only: the page decides which version's words it
 * shows (`tone` 'mine' turns the paper and the thread lilac, so his own
 * reading can never be mistaken for what she received).
 *
 * The picture's line (`caption`, picture-caption DESIGN.md 2026-10-30): the
 * sender speaks first, so it comes right after the note, before the title:
 * the picture, the sender's words, the Mystic's line, then the title. It
 * shows only once the picture has loaded, never under an empty frame.
 *
 * A card with no phrase (a package sent before phrases existed) just has none:
 * no gap, no placeholder. A picture that fails to load leaves the note at the
 * top of the paper, never an empty frame.
 */
import { computed, ref, watch } from 'vue'
import { togetherLabel } from '@/utils/gift-letter'
import CaptionBeats from '@/components/ui/share/CaptionBeats.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  image: { type: String, default: '' },
  note: { type: String, default: '' },
  fromName: { type: String, default: '' },
  title: { type: String, default: '' },
  // The picture's line, written to them ('' on an older package).
  caption: { type: String, default: '' },
  readerName: { type: String, default: '' },
  readerAvatar: { type: String, default: '' },
  // [{ name, reversed, image_url, position, phrase, text }]
  cards: { type: Array, default: () => [] },
  summary: { type: String, default: '' },
  tone: { type: String, default: 'hers' },   // 'hers' | 'mine'
  // The gift room (2026-10-22): a card's art opens its detail ('card-detail', index).
  zoomable: { type: Boolean, default: false },
})
const emit = defineEmits(['card-detail'])

const imageFailed = ref(false)
const imageLoaded = ref(false)
watch(() => props.image, () => { imageFailed.value = false; imageLoaded.value = false })
const showImage = computed(() => !!props.image && !imageFailed.value)
const showCaption = computed(() => !!props.caption && showImage.value && imageLoaded.value)
const failedArt = ref({})
const together = computed(() => togetherLabel(props.cards.length))
const eyebrow = computed(() => props.fromName ? `${props.fromName} was thinking of you` : 'Someone was thinking of you')
</script>

<template>
  <article class="gl" :class="['tone-' + tone, { 'no-lead': !showImage }]" data-testid="gift-letter" :data-tone="tone">
    <div v-if="showImage" class="gl-lead">
      <img :src="image" :alt="caption" data-testid="gift-letter-image" @load="imageLoaded = true" @error="imageFailed = true" />
    </div>

    <div class="gl-note" data-testid="gift-letter-note">
      <span class="gl-seal" aria-hidden="true">✦</span>
      <p class="gl-from">{{ eyebrow }}</p>
      <!-- The send screen's desktop pane puts the editable note here. -->
      <slot name="note"><p v-if="note" class="gl-note-text">{{ note }}</p></slot>
    </div>

    <p v-if="showCaption" class="gl-cap" data-testid="gift-letter-caption"><CaptionBeats :text="caption" /></p>

    <header v-if="title || readerName" class="gl-head">
      <h1 v-if="title" class="gl-title" data-testid="gift-letter-title">{{ title }}</h1>
      <p v-if="readerName" class="gl-by">
        <img v-if="readerAvatar" :src="readerAvatar" alt="" />
        <span>read by <b>{{ readerName }}</b></span>
      </p>
    </header>

    <div v-if="cards.length" class="gl-thread">
      <section v-for="(c, i) in cards" :key="i" class="gl-row" :class="{ 'no-phrase': !c.phrase }" data-testid="gift-letter-card">
        <div class="gl-art" :class="{ 'gl-zoom': zoomable }" :role="zoomable ? 'button' : null" :tabindex="zoomable ? 0 : null"
             :aria-label="zoomable ? `${c.name}: what it means` : null"
             @click="zoomable && emit('card-detail', i)" @keydown.enter="zoomable && emit('card-detail', i)">
          <img v-if="c.image_url && !failedArt[i]" :src="c.image_url" :alt="c.name" :class="{ rev: c.reversed }"
               @error="failedArt = { ...failedArt, [i]: true }" />
          <span v-else class="gl-art-blank" aria-hidden="true"></span>
        </div>
        <div class="gl-words">
          <p class="gl-pos">
            <template v-if="c.position">{{ c.position }}<span class="gl-dot" aria-hidden="true"> · </span></template><span class="gl-card">{{ c.name }}<template v-if="c.reversed">, reversed</template></span>
          </p>
          <h2 v-if="c.phrase" class="gl-phrase" data-testid="gift-letter-phrase">{{ c.phrase }}</h2>
          <p v-if="c.text" class="gl-passage">{{ c.text }}</p>
        </div>
      </section>
    </div>

    <section v-if="summary" class="gl-together" data-testid="gift-letter-together">
      <span class="gl-knot" aria-hidden="true"></span>
      <p v-if="together" class="gl-pos gl-together-label">{{ together }}</p>
      <p class="gl-summary">{{ summary }}</p>
    </section>
    <slot name="end"></slot>
  </article>
</template>

<style scoped>
/* Tokens from the approved mockups (round 2). */
.gl {
  --paper: #f4ecdc; --paper2: #fbf6ec; --paper-edge: #e4d6b8;
  --thread: #c9a94e; --thread-lt: #e7c983; --gold-dk: #8a6a2a;
  --ink: #2a1845; --ink2: #4a3a63; --ink3: #3d2f55; --hush: #7a6a8f;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative; container-type: inline-size; overflow: hidden;
  background: var(--paper); border-radius: 18px; color: var(--ink);
  box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.9);
  padding-bottom: 6px;
  transition: background-color 220ms ease;
}
/* His own reading: lilac paper and a lilac thread. */
.gl.tone-mine { --paper: #efe7f6; --paper2: #f8f3fb; --paper-edge: #ddd0ec; --thread: #a98be0; --thread-lt: #c9b6ee; }

.gl-lead { position: relative; aspect-ratio: 16 / 10; overflow: hidden; background: #2a1845; }
.gl-lead img { display: block; width: 100%; height: 100%; object-fit: cover; }
.gl-lead::after { content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(14,7,25,0.25), rgba(14,7,25,0) 35%, rgba(14,7,25,0) 55%, rgba(14,7,25,0.55)); }

.gl-note {
  position: relative; z-index: 2; margin: -58px 14px 0; padding: 26px 20px 18px;
  background: var(--paper2); border: 1px solid var(--paper-edge); border-radius: 14px;
  box-shadow: 0 18px 30px -22px rgba(42, 24, 69, 0.7);
  transition: background-color 220ms ease, border-color 220ms ease;
}
.gl.no-lead .gl-note { margin-top: 30px; }
.gl-seal {
  position: absolute; top: -17px; left: 50%; transform: translateX(-50%);
  width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center;
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%);
  box-shadow: 0 3px 8px rgba(42, 24, 69, 0.45), inset 0 0 0 3px rgba(255, 240, 200, 0.35);
  color: #5a3f0f; font: 700 14px/1 var(--display);
}
.gl-from { margin: 2px 0 10px; text-align: center; font: 600 11px/1.4 var(--display); letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--gold-dk); text-wrap: balance; }
.gl-note-text { margin: 0; font: italic 500 19px/1.45 var(--serif); color: var(--ink); white-space: pre-line;
  text-wrap: pretty; overflow-wrap: anywhere; }

/* The picture's line: after the note, before the title; it rises in. */
.gl-cap { margin: 20px auto 0; padding: 0 28px; max-width: calc(34ch + 56px); box-sizing: border-box; text-align: center; text-wrap: balance;
  font: italic 500 17px/1.4 var(--serif); color: #5a4a72; animation: gl-cap-in 520ms cubic-bezier(.2, .7, .2, 1) 280ms both; }
@keyframes gl-cap-in { from { opacity: 0; transform: translateY(6px); } }
.gl-head { padding: 26px 22px 2px; text-align: center; }
.gl-cap + .gl-head { padding-top: 12px; }
.gl-title { margin: 0; font: 600 22px/1.22 var(--display); color: var(--ink); text-wrap: balance; overflow-wrap: anywhere; }
.gl-by { margin: 12px 0 0; display: inline-flex; align-items: center; gap: 8px; font: 500 12.5px/1.3 var(--ui); color: var(--hush); }
.gl-by img { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; border: 1px solid var(--thread); }
.gl-by b { color: var(--ink2); font-weight: 600; }

/* The thread: one line the cards hang on, through the middle of the art. */
.gl-thread { --art: 72px; --pad: 20px; --gap: 16px; position: relative; padding: 24px var(--pad) 4px; }
.gl-thread::before { content: ''; position: absolute; left: calc(var(--pad) + var(--art) / 2); top: 0; bottom: 0; width: 1px;
  background: linear-gradient(180deg, transparent, var(--thread) 6%, var(--thread) 100%); }
.gl-row { position: relative; display: grid; grid-template-columns: var(--art) minmax(0, 1fr); gap: var(--gap); padding: 0 0 26px; }
.gl-art { position: relative; width: var(--art); }
.gl-art img, .gl-art-blank { display: block; width: var(--art); aspect-ratio: 2 / 3; object-fit: cover; border-radius: 7px;
  border: 1px solid var(--thread); background: #2a1845; box-shadow: 0 10px 18px -10px rgba(42, 24, 69, 0.7); }
.gl-art img.rev { transform: rotate(180deg); }
.gl-art.gl-zoom { cursor: zoom-in; }
.gl-art.gl-zoom:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 3px; border-radius: 8px; }
.gl-words { min-width: 0; }
.gl-pos { margin: 2px 0 5px; font: 600 10.5px/1.35 var(--display); letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold-dk); }
.gl-dot { letter-spacing: 0; color: var(--hush); }
.gl-card { font: italic 500 13px/1.3 var(--serif); letter-spacing: 0.02em; text-transform: none; color: var(--hush); }
.gl-phrase { margin: 0 0 6px; font: 700 21px/1.15 var(--serif); color: var(--ink); text-wrap: balance; overflow-wrap: anywhere; }
.gl-passage { margin: 0; font: 500 16.5px/1.5 var(--serif); color: var(--ink3); text-wrap: pretty; overflow-wrap: break-word; }
.gl-row.no-phrase .gl-pos { margin-bottom: 6px; }

/* "All four together": where the thread ends, a knot. */
.gl-together { position: relative; margin: 0 20px; padding: 4px 0 24px 52px; }
.gl-knot { position: absolute; left: 30.5px; top: 6px; width: 11px; height: 11px; transform: rotate(45deg);
  background: var(--thread); box-shadow: 0 0 0 4px var(--paper); }
.gl-together-label { margin-bottom: 7px; }
.gl-summary { margin: 0; font: 500 17.5px/1.55 var(--serif); color: var(--ink); text-wrap: pretty; overflow-wrap: break-word; }
/* The thread runs down to the knot, never past it. */
.gl-thread + .gl-together::before { content: ''; position: absolute; left: 36px; top: -4px; height: 10px; width: 1px; background: var(--thread); }

/* A wide letter (a tablet, or the desktop's letter column). */
@container (min-width: 620px) {
  .gl-lead { aspect-ratio: 21 / 9; }
  .gl-note { margin: -84px auto 0; max-width: 560px; padding: 30px 30px 22px; }
  .gl.no-lead .gl-note { margin-top: 40px; }
  .gl-note-text { font-size: 21px; }
  .gl-cap { font-size: 19px; margin-top: 26px; }
  .gl-head { padding: 30px 40px 4px; }
  .gl-cap + .gl-head { padding-top: 12px; }
  .gl-title { font-size: 28px; }
  .gl-thread { --art: 118px; --pad: 48px; --gap: 28px; padding-top: 32px; }
  .gl-row { padding-bottom: 32px; }
  .gl-pos { font-size: 11px; }
  .gl-card { font-size: 14px; }
  .gl-phrase { font-size: 26px; }
  .gl-passage { font-size: 18.5px; max-width: 60ch; }
  .gl-together { margin: 0 48px; padding-left: 88px; padding-bottom: 32px; }
  .gl-knot { left: 53.5px; }
  .gl-thread + .gl-together::before { left: 59px; }
  .gl-summary { font-size: 19.5px; max-width: 62ch; }
}
@container (max-width: 359px) {
  .gl-thread { --art: 60px; --pad: 16px; --gap: 14px; }
  .gl-together { margin: 0 16px; padding-left: 44px; }
  .gl-knot { left: 24.5px; }
  .gl-thread + .gl-together::before { left: 30px; }
  .gl-note { margin-left: 10px; margin-right: 10px; padding-left: 16px; padding-right: 16px; }
  .gl-phrase { font-size: 19.5px; }
}
@media (prefers-reduced-motion: reduce) {
  .gl, .gl-note { transition: none; }
  .gl-cap { animation: none; }
}
</style>
